import { useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import WidgetPlaceholder from "./WidgetPlaceholder";
import { findSentimentLevel } from "./sentimentScale";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";
import "./DonutPieWidget.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

type DonutRow = {
  name: string;
  value: number;
  percentage?: number;
};

type DonutMetadata = {
  total_pa_cases?: number;
};

type Props = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

// ─── Colors ───────────────────────────────────────────────────────────────────

// Approval-outcome vocabulary: approvals on the green ramp (first-pass
// darker than appeal), "pending" a deliberate recessive slate (a state, not
// an identity hue), denied the shared denial red. Checked before the
// sentiment scale below since it's a disjoint vocabulary. Slices carry 2px
// white gaps and a labeled legend, so color is never the only encoding.
const OUTCOME_COLORS: Array<{ match: RegExp; color: string }> = [
  { match: /appeal/i, color: "#10B981" },
  { match: /approv/i, color: "#15803D" },
  { match: /pending/i, color: "#64748B" },
  { match: /denie|denial/i, color: "#DC2626" },
];

// Fallback pool for categories neither vocabulary below recognizes (fixed order).
const FALLBACK_COLORS = ["#2563EB", "#D97706", "#7C3AED", "#0D9488"];

const colorForCategory = (name: string, fallbackIndex: number): string => {
  const outcomeHit = OUTCOME_COLORS.find((c) => c.match.test(name));
  if (outcomeHit) return outcomeHit.color;
  // Sentiment-scale names (Positive/Mostly Positive/Neutral/Mixed/Mostly
  // Negative/Negative) go through the shared scale in sentimentScale.ts, so
  // this widget never shows a different color for the same category than
  // SentimentSplitWidget does on the same dashboard.
  const sentimentHit = findSentimentLevel(name);
  if (sentimentHit) return sentimentHit.color;
  return FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length] ?? "#64748B";
};

// ─── Data unwrap ──────────────────────────────────────────────────────────────

type DonutEnvelope = {
  rows: DonutRow[];
  metadata: DonutMetadata;
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

// Accepts either the full API response ({ data: rows, metadata,
// has_download_options, download_options }) or the bare rows array
// (WidgetRenderer strips the envelope before passing data down).
// has_download_options/download_options are siblings of .data, not nested
// under .metadata, per the backend response shape.
const unwrap = (raw: unknown): DonutEnvelope => {
  if (Array.isArray(raw)) return { rows: raw as DonutRow[], metadata: {} };
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return {
        rows: obj.data as DonutRow[],
        metadata: (obj.metadata ?? {}) as DonutMetadata,
        hasDownloadOptions: obj.has_download_options as boolean | undefined,
        downloadOptions: obj.download_options as
          | WidgetDownloadOptions
          | undefined,
      };
    }
  }
  return { rows: [], metadata: {} };
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function DonutPieWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { rows, metadata, hasDownloadOptions, downloadOptions } = useMemo(
    () => unwrap(data),
    [data],
  );

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={hasDownloadOptions ? downloadOptions : undefined}
    />
  );

  const total = useMemo(
    () =>
      metadata.total_pa_cases ??
      rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0),
    [rows, metadata],
  );

  const slices = useMemo(
    () =>
      rows.map((r, i) => {
        const value = Number(r.value) || 0;
        const pct = r.percentage ?? (total > 0 ? (value / total) * 100 : 0);
        return {
          name: r.name,
          y: value,
          color: colorForCategory(r.name, i),
          custom: { pct },
        };
      }),
    [rows, total],
  );

  // Keep the chart sized to its flex container. Deps (not []) because on
  // first mount isLoading is still true, so this render returns
  // WidgetPlaceholder and wrapperRef.current is null — the effect would
  // bail out and never re-run once the real chart div appears, leaving
  // nothing to size the chart once it mounts. Re-running when the loading/
  // empty gate flips lets it attach to the real node.
  //
  // Uses chart.setSize(el.clientWidth, el.clientHeight) instead of
  // chart.reflow(): reflow() re-measures the container via Highcharts' own
  // getStyle()-based logic, which reads our container's CSS height:100%
  // literally and can't resolve it this many flex levels deep, so it kept
  // falling back to Highcharts' hardcoded 400px default (confirmed via
  // devtools: SVG stuck at exactly 400 regardless of the real, correctly
  // laid-out container height). Passing our own getBoundingClientRect-based
  // measurement bypasses that broken detection entirely.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    let raf = 0;
    const resize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const chart = (chartRef.current as any)?.chart;
        if (!chart || !el) return;
        chart.setSize(el.clientWidth, el.clientHeight, false);
      });
    };
    const timer = setTimeout(resize, 0);
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isLoading, rows.length]);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: {
        animation: false,
        backgroundColor: "transparent",
        margin: [4, 4, 4, 4],
        style: { fontFamily: "inherit" },
      },
      title: { text: "" },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      accessibility: { enabled: false },
      tooltip: {
        useHTML: true,
        formatter(this: any) {
          const pct = this.point?.options?.custom?.pct ?? 0;
          return `<div style="padding:2px 4px;">
            <div style="font-size:12px;font-weight:700;margin-bottom:2px;">${this.point.name}</div>
            <div style="font-size:11px;">Cases: <b>${Number(this.y).toLocaleString()}</b></div>
            <div style="font-size:11px;">Share: <b>${pct.toFixed(1)}%</b></div>
          </div>`;
        },
      },
      plotOptions: {
        pie: {
          innerSize: "68%",
          size: "92%",
          borderWidth: 2,
          borderColor: "#FFFFFF",
          animation: false,
          dataLabels: { enabled: false },
          states: {
            hover: { halo: { size: 6 }, brightness: 0.05 },
            inactive: { opacity: 0.4 },
          },
        },
      },
      series: [
        {
          type: "pie",
          name: "Outcomes",
          data: slices,
        },
      ],
    }),
    [slices],
  );

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={240} />;
  }

  if (rows.length === 0) {
    return (
      <>
        {downloadMenu}
        <div
          className="d-flex align-items-center justify-content-center h-100"
          style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}
        >
          No outcome data available.
        </div>
      </>
    );
  }

  return (
    <div className="donut-widget">
      {downloadMenu}
      <div className="donut-widget__chart" ref={wrapperRef}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
        />
        <div className="donut-widget__center">
          <span
            style={{
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.1,
              color: "var(--bs-body-color)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {total.toLocaleString()}
          </span>
          <span style={{ fontSize: 10.5, color: "var(--bs-secondary-color)" }}>
            {metadata.total_pa_cases !== undefined ? "PA cases" : "total"}
          </span>
        </div>
      </div>

      <div className="donut-widget__legend">
        {slices.map((s, i) => (
          <div
            key={s.name}
            className="donut-widget__legend-row"
            style={{ animationDelay: `${i * 30}ms` }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span
              className="text-truncate"
              style={{ flex: 1, fontSize: 13, color: "var(--bs-body-color)" }}
              title={s.name}
            >
              {s.name}
            </span>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--bs-secondary-color)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {Number(s.y).toLocaleString()}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                minWidth: 48,
                textAlign: "right",
                color: "var(--bs-body-color)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {s.custom.pct.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
